import React, { useState, useRef, useEffect } from 'react';

const ZONES = ['melee', 'close', 'mid', 'far', 'distant'];
const ZONE_RADIUS = { melee: 0.18, close: 0.34, mid: 0.52, far: 0.7, distant: 0.88 };
const SVG_SIZE = 320; // viewBox size (fixed coordinate space)

export default function TacticalRadar({ zones, players, enemies, onMove, onClose }) {
  const [dragging, setDragging] = useState(null);
  const [sweepAngle, setSweepAngle] = useState(0);
  const [pinging, setPinging] = useState({});
  const [pos, setPos] = useState({ x: window.innerWidth - 420, y: 80 });
  const dragRef = useRef(null);

  // Sweep rotation
  useEffect(() => {
    let frame;
    let angle = 0;
    const rotate = () => {
      angle = (angle + 1.5) % 360;
      setSweepAngle(angle);
      frame = requestAnimationFrame(rotate);
    };
    frame = requestAnimationFrame(rotate);
    return () => cancelAnimationFrame(frame);
  }, []);

  // Ping detection
  useEffect(() => {
    const tokens = getTokens();
    const newPings = {};
    tokens.forEach(t => {
      const diff = Math.abs(((sweepAngle - t.angle) + 360) % 360);
      if (diff < 8 || diff > 352) {
        newPings[t.id] = Date.now();
      }
    });
    if (Object.keys(newPings).length > 0) {
      setPinging(prev => ({ ...prev, ...newPings }));
    }
  }, [sweepAngle]);

  function getTokens() {
    const tokens = [];
    const zoneGroups = {};
    (players || []).filter(p => p.active).forEach(p => {
      const zone = (zones && zones[p.char_name]) || 'close';
      if (!zoneGroups[zone]) zoneGroups[zone] = [];
      zoneGroups[zone].push({ id: p.discord_id, name: p.char_name, type: 'player' });
    });
    (enemies || []).forEach(e => {
      const zone = (zones && zones[e.name]) || 'close';
      if (!zoneGroups[zone]) zoneGroups[zone] = [];
      zoneGroups[zone].push({ id: 'e_' + e.name, name: e.name, type: 'enemy' });
    });
    Object.entries(zoneGroups).forEach(([zone, group]) => {
      const radius = ZONE_RADIUS[zone] || 0.34;
      group.forEach((token, i) => {
        const angle = (360 / group.length) * i + (ZONES.indexOf(zone) * 37);
        tokens.push({ ...token, zone, radius, angle: angle % 360 });
      });
    });
    return tokens;
  }

  // Convert polar to percentage (0-100%)
  function polarToPercent(radius, angleDeg) {
    const rad = (angleDeg - 90) * (Math.PI / 180);
    return {
      x: 50 + radius * 50 * Math.cos(rad),
      y: 50 + radius * 50 * Math.sin(rad),
    };
  }

  // Titlebar drag only
  const onTitleMouseDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX - pos.x, startY: e.clientY - pos.y };
    const onMouseMove = (ev) => setPos({ x: ev.clientX - dragRef.current.startX, y: ev.clientY - dragRef.current.startY });
    const onMouseUp = () => { document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onTokenDragStart = (e, token) => {
    e.preventDefault(); // Disable native drag
  };

  const onTokenMouseDown = (e, token) => {
    e.stopPropagation();
    const container = e.target.closest('.radar-container');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setDragging({ ...token, dragX: (50), dragY: (50) });

    const onMouseMove = (ev) => {
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;
      setDragging(prev => prev ? { ...prev, dragX: (x / rect.width) * 100, dragY: (y / rect.height) * 100 } : null);
    };

    const onMouseUp = (ev) => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      const x = ev.clientX - rect.left - cx;
      const y = ev.clientY - rect.top - cy;
      const dist = Math.sqrt(x*x + y*y) / (rect.width / 2);
      let closest = 'close';
      let minDiff = Infinity;
      ZONES.forEach(z => {
        const diff = Math.abs(dist - ZONE_RADIUS[z]);
        if (diff < minDiff) { minDiff = diff; closest = z; }
      });
      if (onMove) onMove(token.name, closest);
      setDragging(null);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onZoneDrop = (e, zone) => {
    e.preventDefault();
    if (dragging && onMove) onMove(dragging.name, zone);
    setDragging(null);
  };

  const tokens = getTokens();
  const s = SVG_SIZE;
  const half = s / 2;

  return (
    <div className="radar-window" style={{ left: pos.x, top: pos.y }}>
      <div className="radar-titlebar" onMouseDown={onTitleMouseDown}>
        <span>🗺️ TACTICAL RADAR</span>
        <button className="radar-close" onClick={onClose}>✕</button>
      </div>
      <div className="radar-container">
        {/* Conic gradient sweep */}
        <div className="radar-sweep-overlay" style={{ transform: `rotate(${sweepAngle}deg)` }} />
        {/* SVG radar */}
        <svg viewBox={`0 0 ${s} ${s}`} className="radar-svg" preserveAspectRatio="xMidYMid meet">
          {ZONES.map(zone => (
            <circle key={zone} cx={half} cy={half} r={ZONE_RADIUS[zone] * half} className={`radar-ring ${dragging ? 'ring-highlight' : ''}`} />
          ))}
          {[0, 45, 90, 135, 180, 225, 270, 315].map(a => {
            const rad = (a - 90) * Math.PI / 180;
            return <line key={a} x1={half} y1={half} x2={half + half*0.9*Math.cos(rad)} y2={half + half*0.9*Math.sin(rad)} className="radar-gridline" />;
          })}
          <circle cx={half} cy={half} r={3} fill="var(--accent)" />
          {ZONES.map(zone => (
            <text key={'lbl_'+zone} x={half + 4} y={half - ZONE_RADIUS[zone]*half + 10} className="radar-zone-text">{zone.toUpperCase()}</text>
          ))}
        </svg>

        {/* Tokens positioned with percentages (scale with container) */}
        {tokens.map(token => {
          const isDragging = dragging && dragging.id === token.id;
          const { x, y } = isDragging
            ? { x: dragging.dragX, y: dragging.dragY }
            : polarToPercent(token.radius, token.angle);
          const isPinging = pinging[token.id] && (Date.now() - pinging[token.id] < 500);
          return (
            <div
              key={token.id}
              className={`radar-token ${token.type} ${isPinging ? 'ping' : ''} ${isDragging ? 'dragging' : ''}`}
              style={{ left: `calc(${x}% - 6px)`, top: `calc(${y}% - 6px)` }}
              onMouseDown={e => onTokenMouseDown(e, token)}
              title={token.name}
            >
              <span className="token-label">{token.name.slice(0, 3)}</span>
            </div>
          );
        })}

        {/* Drop zones (percentage-based) */}
        {ZONES.map(zone => {
          const r = ZONE_RADIUS[zone] * 100;
          return (
            <div
              key={'drop_'+zone}
              className="radar-dropzone"
              style={{
                position: 'absolute',
                left: `${50 - r/2}%`, top: `${50 - r/2}%`,
                width: `${r}%`, height: `${r}%`,
                borderRadius: '50%',
              }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => onZoneDrop(e, zone)}
            />
          );
        })}
      </div>
      <div className="radar-legend">
        <span className="legend-player">● Player</span>
        <span className="legend-enemy">● Enemy</span>
      </div>
    </div>
  );
}
